/**
 * Unified Supabase Authentication Configuration
 *
 * This module provides a single source of truth for all Supabase authentication
 * across client-side, server-side, and middleware contexts.
 */

import { logger } from '@/lib/logger'

const envLog = logger.child({ module: 'env-validation' })

// ── Required environment variables (crash if missing) ──────────────────
const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY
const supabaseServiceKey = process.env.SUPABASE_SERVICE_ROLE_KEY

if (!supabaseUrl) {
  throw new Error('Missing NEXT_PUBLIC_SUPABASE_URL environment variable')
}

if (!supabaseAnonKey) {
  throw new Error('Missing NEXT_PUBLIC_SUPABASE_ANON_KEY environment variable')
}

// ── Optional environment variables (warn if missing) ───────────────────
if (!supabaseServiceKey) {
  envLog.warn('SUPABASE_SERVICE_ROLE_KEY is not set — admin operations will fail')
}

if (!process.env.HELCIM_API_TOKEN) {
  envLog.warn('HELCIM_API_TOKEN is not set — payment routes will be unavailable')
}

if (!process.env.RESEND_API_KEY) {
  envLog.warn('RESEND_API_KEY is not set — email sending will be unavailable')
}

if (!process.env.ANTHROPIC_API_KEY) {
  envLog.warn('ANTHROPIC_API_KEY is not set — AI chat features will be unavailable')
}

export const authConfig = {
  url: supabaseUrl,
  anonKey: supabaseAnonKey,
  serviceKey: supabaseServiceKey,
} as const

// Common Supabase client options with unique storage keys for each context
export const commonClientOptions = {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
} as const

// PHASE 1 FIX: Unified storage key for all authentication contexts
// This eliminates session conflicts between different client instances
const UNIFIED_STORAGE_KEY = 'jwa-auth-unified'

// Context-specific client options with unified storage key
export const browserClientOptions = {
  ...commonClientOptions,
  auth: {
    ...commonClientOptions.auth,
    storageKey: UNIFIED_STORAGE_KEY,
  },
} as const

export const serverRouteClientOptions = {
  ...commonClientOptions,
  auth: {
    ...commonClientOptions.auth,
    storageKey: UNIFIED_STORAGE_KEY,
  },
} as const

export const middlewareClientOptions = {
  ...commonClientOptions,
  auth: {
    ...commonClientOptions.auth,
    storageKey: UNIFIED_STORAGE_KEY,
  },
} as const

// Admin client options (server-side only)
export const adminClientOptions = {
  auth: {
    autoRefreshToken: false,
    persistSession: false,
  },
} as const
